/**
 * Card de métrica para detalhes de campanha
 * Padrão visual idêntico ao MetricCard de Departments.tsx (DealListKpiCards)
 * Compacto, sólido, sem gradiente nem hover-lift
 */

import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { LucideIcon } from 'lucide-react';

interface CampaignMetricCardProps {
  /** Título do card */
  title: string;
  /** Valor numérico */
  value: number;
  /** Ícone Lucide */
  icon: LucideIcon;
  /** Variante de cor pastel */
  variant: 'primary' | 'success' | 'info' | 'purple' | 'danger' | 'warning' | 'neutral';
}

/**
 * Mapeamento de variantes para cores pastel sólidas (sem gradiente)
 */
const variantStyles: Record<CampaignMetricCardProps['variant'], { cardBg: string; iconColor: string }> = {
  primary: {
    cardBg: 'bg-purple-100 dark:bg-purple-900/30',
    iconColor: 'text-purple-700 dark:text-purple-300',
  },
  success: {
    cardBg: 'bg-lime-100 dark:bg-green-900/30',
    iconColor: 'text-green-700 dark:text-green-300',
  },
  info: {
    cardBg: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'text-blue-700 dark:text-blue-300',
  },
  purple: {
    cardBg: 'bg-cyan-100 dark:bg-cyan-900/30',
    iconColor: 'text-cyan-700 dark:text-cyan-300',
  },
  danger: {
    cardBg: 'bg-red-100 dark:bg-red-900/30',
    iconColor: 'text-red-700 dark:text-red-300',
  },
  warning: {
    cardBg: 'bg-amber-100 dark:bg-amber-900/30',
    iconColor: 'text-amber-700 dark:text-amber-300',
  },
  neutral: {
    cardBg: 'bg-muted',
    iconColor: 'text-muted-foreground',
  },
};

/**
 * Card compacto de métrica — ícone+título à esquerda, valor à direita.
 */
export function CampaignMetricCard({ title, value, icon: Icon, variant }: CampaignMetricCardProps) {
  const styles = variantStyles[variant];

  return (
    <Card className={cn('border-0 shadow-none cursor-default', styles.cardBg)}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className={cn('h-4 w-4 shrink-0', styles.iconColor)} />
            <p className="text-xs font-medium truncate text-foreground">{title}</p>
          </div>
          <p className="text-xl font-bold leading-tight shrink-0 text-foreground">
            {value.toLocaleString('pt-BR')}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
